<template>
    <v-expansion-panels :value="displayForm">
        <v-dialog v-model="showErrorMessage" width="500px">
            <v-card>
                <p class="p-gs">{{ errorMessage }}</p>
                <v-card-actions>
                    <v-btn color="primary" text @click="errorMessage = null">
                        {{ $t('forms.errors.close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-expansion-panel :key="'search'">
            <v-expansion-panel-header>
                <v-card-title>{{ $t('genericSearch.searchParams') }}</v-card-title>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <v-form @submit.prevent>
                    <v-container>
                        <v-row v-for="(line, index) in fields" :key="index">
                            <v-col v-for="(field, i) in line" :key="i" cols="12" :md="nbrColumns">
                                <v-autocomplete
                                    v-if="field.type === 'select'"
                                    v-model="formValues[field.name]"
                                    :items="selects[field.databind]"
                                    cache-items
                                    clearable
                                    color="#3200E6"
                                    :required="field.required"
                                    :rules="field.required ? rules.required : undefined"
                                    @blur="isValid = isValided()"
                                >
                                    <template v-slot:label
                                        >{{ getTranslation(field.label) }}<b v-if="field.required">*</b></template
                                    >
                                </v-autocomplete>
                                <v-textarea
                                    color="#3200E6"
                                    v-else-if="field.type === 'textarea'"
                                    v-model="formValues[field.name]"
                                    placeholder=""
                                    persistent-placeholder
                                    :required="field.required"
                                    :rules="field.required ? rules.required : undefined"
                                    @blur="isValid = isValided()"
                                >
                                    <template v-slot:label
                                        >{{ getTranslation(field.label) }}<b v-if="field.required">*</b></template
                                    >
                                </v-textarea>
                                <v-dialog
                                    v-else-if="field.type === 'date'"
                                    :ref="`dialog_${field.name}`"
                                    v-model="modals[field.name]"
                                    :return-value.sync="formValues[field.name]"
                                    width="290px"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="formValues[field.name]"
                                            prepend-icon="mdi-calendar"
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                            @blur="
                                                () => {
                                                    parseDateInput(formValues[field.name], field.name);
                                                    isValid = isValided();
                                                }
                                            "
                                            @click:clear="formValues[field.name] = null"
                                            color="#3200E6"
                                            :required="field.required"
                                            :rules="field.required ? rules.required : undefined"
                                        >
                                            <template v-slot:label
                                                >{{ getTranslation(field.label) }}<b v-if="field.required">*</b></template
                                            >
                                        </v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="formValues[field.name]"
                                        scrollable
                                        color="#3200E6"
                                        :title-date-format="data => getTitleDateFormat(data, field.label)"
                                    >
                                        <v-spacer></v-spacer>
                                        <v-btn text color="#3200E6" @click="closeModal(field.name)"> Cancel </v-btn>
                                        <v-btn
                                            text
                                            color="#3200E6"
                                            @click="$refs[`dialog_${field.name}`][0].save(formValues[field.name])"
                                        >
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-dialog>

                                <v-combobox
                                    v-else
                                    color="#3200E6"
                                    v-model="formValues[field.name]"
                                    :required="field.required"
                                    :rules="field.required ? rules.required : undefined"
                                    @blur="isValid = isValided()"
                                    :items="suggestion[field.name]"
                                    append-icon=""
                                >
                                    <template v-slot:label
                                        >{{ getTranslation(field.label) }}<b v-if="field.required">*</b></template
                                    >
                                </v-combobox>
                            </v-col>
                        </v-row>
                        <v-btn class="action" :disabled="!isValid" @click="submit">{{ $t('forms.submit') }}</v-btn>
                    </v-container>
                </v-form>
            </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
import moment from 'moment';
import Vue from 'vue';
import { mapGetters, mapMutations } from 'vuex';

import { api } from '@/utils/api';

export default {
    name: 'search-bar-gs',
    props: ['clientId'],
    data() {
        const suggests = JSON.parse(localStorage.getItem('historySuggestionGnS') || '{}');
        const suggestion = suggests[this.clientId] || {};
        return {
            formValues: {},
            fields: [],
            selects: null,
            modals: {},
            titles: {},
            suggestion,
            displayForm: 0,
            isValid: false,
            nbrColumns: -1,
            errorMessage: null,
            rules: {
                required: [val => (val || '').length > 0 || this.$t('forms.errors.required')]
            }
        };
    },
    computed: {
        ...mapGetters(['getPortletId']),
        showErrorMessage() {
            return !!this.errorMessage;
        }
    },
    methods: {
        ...mapMutations(['setGSPortletId', 'setCurrentPortlet', 'setListResult']),
        ...mapMutations(['setLoading']),
        getTranslation(refType = 'name') {
            const key = `genericSearch.${this.clientId}.${this.getPortletId}.searchForm.${refType}`;
            return this.$te(key) ? this.$t(key) : this.$t(`genericSearch.default.searchForm.${refType}`);
        },
        parseDateInput(input, fieldName) {
            const newDate = new Date(input);
            if (input && input.length === 10 && !isNaN(newDate.getTime())) {
                // force format iso date
                Vue.set(
                    this.formValues,
                    fieldName,
                    new Date(newDate.valueOf() - newDate.getTimezoneOffset() * 60000).toISOString().substring(0, 10)
                );
            } else {
                Vue.set(this.formValues, fieldName, '');
            }
        },
        isValided(fields = this.fields) {
            let valided = true;
            for (const field of fields) {
                if (typeof field === 'object' && field.length) {
                    if (!this.isValided(field)) {
                        valided = false;
                    }
                } else if (field.required) {
                    if (!this.formValues[field.name] || this.formValues[field.name].length === 0) {
                        valided = false;
                    }
                }
            }
            return valided;
        },
        getTitleDateFormat(isoDate, label) {
            const title = `<small style="font-size:14px;display:block;">${this.getTranslation(label)}</small>`;
            return `${moment(isoDate).format('ddd, MMM D')}${title}`;
        },
        toggle() {
            this.displayForm = !this.displayForm;
        },
        closeModal(name) {
            Vue.set(this.modals, name, false);
        },
        async setForm() {
            if (!this.clientId) return;

            const portletId = document.location.pathname.substring(document.location.pathname.lastIndexOf('/') + 1);
            const clientConfig = await api.get('config', { clientId: this.clientId, portletId: portletId }, null, 'genericsearch');

            if (!clientConfig) return;
            this.setGSPortletId(clientConfig.id);
            this.setCurrentPortlet(clientConfig.name);
            this.formValues = {
                extractionType: clientConfig.extraction_type,
                customerName: clientConfig.customerName,
                initEndpoint: clientConfig.initEndpoint,
                searchEndpoint: clientConfig.searchEndpoint,
                languageCode: this.$vuetify.lang.current.toUpperCase(),
                deliverySite: clientConfig.deliverySite,
                departureSite: clientConfig.departureSite,
                bodyResource: clientConfig.bodyResource,
                initResource: clientConfig.initResource,
                fields: clientConfig.fields
            };
            const selectableInput = await api.post('init', this.formValues);
            const selectInput = {};
            for (const select in selectableInput) {
                const choice = new Array(selectableInput[select].length);
                for (let i = 0; i < selectableInput[select].length; i++) {
                    const item = {};
                    for (const selectInput in selectableInput[select][i]) {
                        item[selectInput.indexOf('Code') === -1 ? 'text' : 'value'] = selectableInput[select][i][selectInput];
                    }
                    choice[i] = item;
                }
                selectInput[select] = choice;
            }
            this.selects = selectInput;

            this.fields = await api.get('portlet', {
                idClient: this.clientId,
                idPortlet: this.getPortletId
            });
            const lines = this.fields.map(arr => arr.length);
            for (let i = 0; i < lines.length; i++) {
                if (lines[i] > this.nbrColumns) this.nbrColumns = lines[i];
            }
            this.nbrColumns = 12 % this.nbrColumns || this.nbrColumns > 12 ? 12 : 12 / this.nbrColumns;
            for (let i = 0; i < this.fields.length; i++) {
                for (let j = 0; j < this.fields[i].length; j++) {
                    if (this.fields[i][j].type === 'date') {
                        Vue.set(this.modals, this.fields[i][j].name, false);
                        this.formValues[this.fields[i][j].name] = this.fields[i][j].required
                            ? new Date().toISOString().substr(0, 10)
                            : '';
                        this.titles[this.fields[i][j].name] = this.$t(this.fields[i][j].label);
                    } else {
                        this.formValues[this.fields[i][j].name] = null;
                    }
                }
            }
            this.isValid = this.isValided();
        },

        // used to get values from the form
        getTextFiledValues(formValues) {
            let savedValues = this.suggestion || {};
            formValues?.fields?.map(item => {
                if (item.type === 'text') {
                    const inputTextName = item.name;
                    if (formValues[inputTextName]) {
                        if (savedValues && savedValues[inputTextName]) {
                            if (
                                Array.isArray(savedValues[inputTextName]) &&
                                !savedValues[inputTextName].includes(formValues[inputTextName])
                            ) {
                                savedValues[inputTextName].unshift(formValues[inputTextName]);
                                if (savedValues[inputTextName].length == 6) {
                                    savedValues[inputTextName].pop();
                                }
                            } else {
                                savedValues[inputTextName] = [formValues[inputTextName]];
                            }
                        } else {
                            savedValues[inputTextName] = [formValues[inputTextName]];
                        }
                    }
                }
            });
            this.suggestion = savedValues;
        },
        async submit() {
            const data = this.formValues;
            data.languageCode = this.$vuetify.lang.current.toUpperCase();
            this.setLoading(true);
            this.toggle();
            const result = await api.post('search', { ...data });
            // save v-combox inputs in localsotrage
            this.getTextFiledValues(data);
            const suggests = JSON.parse(localStorage.getItem('historySuggestionGnS') || '{}');
            localStorage.setItem(
                'historySuggestionGnS',
                JSON.stringify({
                    ...suggests,
                    [this.clientId]: this.suggestion
                })
            );
            this.setListResult(result.results);
            if (result.errorMessage && !result.errorMessage.includes('.java') && result.errorMessage !== 'NOT FOUND') {
                this.errorModal = true;
                this.errorMessage = result.errorMessage;
            }
            this.setLoading(false);
        }
    },
    created() {
        this.setForm();
    },
    watch: {
        clientId: function (newValue) {
            const suggests = JSON.parse(localStorage.getItem('historySuggestionGnS') || '{}');
            this.suggestion = suggests[this.clientId] || {};
            this.setForm();
        }
    }
};
</script>

<style lang="scss" scoped>
.v-card__title {
    word-break: break-word;
}
.v-label b {
    color: #f00;
    font-weight: bold;
    font-size: 16px;
    margin-left: 5px;
}
.p-gs {
    padding: 20px 20px 0;
}
</style>
